/**
 * 任务队列2.0 主要 Composable
 * 
 * @description 整合队列管理、用户策略、埋点追踪等功能的主要入口
 * @author Frontend Architect
 * @version 2.0.0
 */

import { computed } from 'vue';
import { useUserStore } from '@/store/user';
import { useTaskQueueStore } from '@/store/taskQueue';
import { useArtworkStore } from '@/store/artwork';
import { 
  UserType, 
  QueueStage, 
  GenerationStatus,
  type QueueTask, 
  type CreateQueueTaskParams
} from '@/types/taskQueue';

/** 队列任务状态 Composable */
export function useTaskQueue2() {
  const userStore = useUserStore();
  const taskQueueStore = useTaskQueueStore();

  /** 检查当前用户是否应该使用新队列 */
  const shouldUseNewQueue = (): boolean => {
    return true;
    try {
      const result = userStore.userInfo.qtest === 'q1';
      return result;
    } catch (error) {
      return false;
    }
  };

  /** 获取用户类型 */
  const getUserType = (): UserType => {
    try {
      // 获取artworkStore中的channel设置
      const artworkStore = useArtworkStore();
      
      // 如果用户选择了标准通道(normal)，无论用户是什么类型都按免费用户策略处理
      if (artworkStore.channel === 'normal') {
        return UserType.FREE;
      }
      
      // 如果用户选择了加速通道(expedite)，则根据用户的会员级别返回对应类型
      const result = userStore.hasMembershipAtLeast('svip')
        ? UserType.SVIP
        : userStore.isMemberUser
          ? UserType.VIP
          : UserType.FREE;
      return result;
    } catch (error) {
      return UserType.FREE;
    }
  };

  /** 启动队列任务 */
  const startQueueTask = (params: CreateQueueTaskParams): QueueTask => {
    return taskQueueStore.startQueueTask(params);
  };

  /** 获取任务状态 */
  const getTaskStatus = (clientId: string): QueueTask | undefined => {
    return taskQueueStore.getTaskStatus(clientId);
  };

  /** 取消任务 */
  const cancelTask = (clientId: string, reason = 'user_cancelled'): void => {
    taskQueueStore.cancelTask(clientId, reason);
  };

  // 返回公共接口
  return {
    // 方法
    startQueueTask,
    getTaskStatus,
    cancelTask,
    shouldUseNewQueue,
    getUserType,
    
    // 计算属性
    activeTasks: computed(() => taskQueueStore.activeTaskList),
    activeTaskCount: computed(() => taskQueueStore.activeTaskCount),
    
    // 清理方法
    cleanup: () => taskQueueStore.cleanup()
  };
}

/**
 * 与现有生图系统的集成接口
 * 用于在现有的 useGenerate 流程中集成新队列系统
 */
export function useTaskQueueIntegration() {
  const { 
    startQueueTask, 
    getTaskStatus,
    shouldUseNewQueue,
    getUserType,
  } = useTaskQueue2();

  /**
   * 处理生图开始
   */
  const handleOnWebSocketInited = (clientId: string, module: number, params?: any) => {
    if (!shouldUseNewQueue()) {
      return;
    }

    try {
      // 启动新队列任务
      const task = startQueueTask({
        clientId,
        module,
        userType: getUserType(),
        useNewQueue: true,
        aspectRatios: params?.aspectRatios,
        callbacks: {
          onUpdate: (task) => {
            // 这里可以添加额外的更新逻辑
          },
          onComplete: (task) => {
            // 任务完成回调
          },
          onError: (task, error) => {
            // 任务错误回调
          }
        }
      });
    } catch (error) {
      // 错误处理
    }
  };

  /**
   * 处理消息更新（排队阶段）
   */
  const handleOnUpdate = (messageData: any, clientId: string) => {
    if (!shouldUseNewQueue()) return;
    
    // 根据消息数据更新队列状态
    const task = getTaskStatus(clientId);
    if (task && task.stage === QueueStage.QUEUING) {
      // 可以根据 messageData 中的排队信息更新任务
      if (messageData.queueNum !== undefined) {
        const taskQueueStore = useTaskQueueStore();
        taskQueueStore.updateTask(clientId, {
          queueNum: messageData.queueNum,
          queueTotalNum: messageData.queueTotalNum || task.queueTotalNum,
        });
      }
    }
  };

  /**
   * 处理生图进行中
   */
  const handleOnGenerating = (clientId: string) => {
    if (!shouldUseNewQueue()) return;
    
    const taskQueueStore = useTaskQueueStore();
    const success = taskQueueStore.handleExternalGenerationStart(clientId);
  };

  /**
   * 处理生图成功
   */
  const handleOnSuccess = (clientId: string) => {
    if (!shouldUseNewQueue()) return;
    
    const taskQueueStore = useTaskQueueStore();
    const success = taskQueueStore.handleExternalGenerationComplete(clientId);
  };

  /**
   * 处理生图错误
   */
  const handleOnError = (clientId: string, error?: string) => {
    if (!shouldUseNewQueue()) return;
    
    const taskQueueStore = useTaskQueueStore();
    const success = taskQueueStore.handleExternalGenerationError(clientId, error);
  };

  return {
    handleOnWebSocketInited,
    handleOnUpdate,
    handleOnGenerating,
    handleOnSuccess,
    handleOnError,
  };
} 